import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';

// Phase 12 Cleanup: `DEPARTMENT_TUTORIAL_CONFIG: ChecklistStep[]` und
// `registerDepartmentTutorials(registry)` wurden entfernt (ADR E4 — nicht
// migriert). Aktive Tour-Definitionen leben unten als `TourConfig`.

/**
 * Dialog-Close-Race-Guard für die Dialog-Steps 4–6:
 *
 * Wenn der User in Step 3 (department-name) direkt auf „Erstellen" klickt,
 * dispatcht `add-edit-department.component.ts` synchron das Event
 * `onboarding:department.created` und ruft danach `dialogRef.close()`.
 * Step 3 rückt via `advanceOnDocumentEvent` sofort vor — aber `dialogRef.
 * close()` startet nur die Close-Animation (ca. 225 ms), in der das
 * `<app-add-edit-department>`-Element noch im DOM steht. Ohne Schutz
 * würden die `skipIf`-Funktionen der Steps 4–6 dann `false` liefern und
 * die Tour würde die Dialog-Steps fälschlich rendern, bevor die Tour-
 * eigene „Dialog geschlossen = tour close"-Logik die Tour beendet.
 *
 * Lösung: `add-edit-department.component.ts` setzt `document._deptCreatedAt`
 * direkt vor dem Event-Dispatch. Steps 4–6 überspringen sich, solange der
 * Zeitstempel frisch ist (< 1500 ms).
 */
function isDeptCreatedFresh(): boolean {
  const ts = (document as any)._deptCreatedAt as number | undefined;
  return typeof ts === 'number' && performance.now() - ts < 1500;
}

function dialogStepSkipIf(): boolean {
  return (
    !document.querySelector('app-add-edit-department') || isDeptCreatedFresh()
  );
}

/**
 * Wartet aktiv, bis das `<app-add-edit-department>`-Element aus dem DOM
 * verschwunden ist (Material-Dialog-Close-Animation vorbei). Cap auf
 * 400 ms, damit die Tour bei unerwarteten Hängern nicht stehen bleibt.
 *
 * Wird vom Outro-Step `departments-table-overview` verwendet, um den
 * Tabellen-Spotlight nicht auf einer noch durch Dialog/Backdrop verdeckten
 * Tabelle landen zu lassen. Statisches `setTimeout(250)` würde immer warten,
 * auch wenn der Dialog längst weg ist (z. B. wenn der User in Step 6 auf
 * Speichern klickt und die Close-Animation parallel zur Tour-Iteration
 * läuft).
 */
async function waitForDeptDialogGone(): Promise<void> {
  const start = performance.now();
  while (
    document.querySelector('app-add-edit-department') &&
    performance.now() - start < 400
  ) {
    await new Promise<void>((resolve) =>
      requestAnimationFrame(() => resolve())
    );
  }
}

/**
 * Kontextuelle In-Dialog-Tour fuer "Abteilung/Projekt hinzufuegen".
 *
 * UX-Entscheidung (Phase 6 UX-Fix): Offensichtliche Felder (name, description)
 * sind raus. Erklaert werden nur die Felder mit Folgewirkung auf andere
 * Objekte (parent, skills, project-manager) oder nicht offensichtlicher
 * Semantik (project-status, Verwaltungs-Buttons).
 */
const AUTO_ADVANCE_ON_BLUR = {
  enabled: true,
  triggerOn: 'blur' as const,
  debounceMs: 300,
  minLength: 1,
};

/**
 * Snapshot der Auswahlliste an einem mat-select via DOM-Attribute.
 * `add-edit-department.component.html` exposed:
 *   - `#parent-department-select`  → `data-department-count` + `data-department-list-loaded`
 *   - `#department-skills-select`  → `data-skill-count`      + `data-skill-list-loaded`
 *
 * Wird zur Render-Zeit der Tour-Steps aufgerufen, um Empty- vs. Populated-
 * Varianten zu wählen. Solange `loaded=false` (initial-flicker), bleiben wir
 * auf der neutralen Default-Variante (`CONTENT`), damit der User nicht kurz
 * den Empty-Hint sieht und dann zurück.
 */
function readSelectSnapshot(
  selectId: string,
  countAttr: string,
  loadedAttr: string,
): { count: number; loaded: boolean } {
  const el = document.getElementById(selectId);
  if (!el) return { count: 0, loaded: false };
  const count = Number(el.getAttribute(countAttr) ?? '0');
  const loaded = el.getAttribute(loadedAttr) === 'true';
  return { count: Number.isFinite(count) ? count : 0, loaded };
}

function parentDeptSnapshot() {
  return readSelectSnapshot(
    'parent-department-select',
    'data-department-count',
    'data-department-list-loaded',
  );
}

function skillsSnapshot() {
  return readSelectSnapshot(
    'department-skills-select',
    'data-skill-count',
    'data-skill-list-loaded',
  );
}

/**
 * Welcome-Tour V2: erste Abteilung anlegen.
 *
 * Fachlich schlanke 6-Step-Struktur (Session Teil 7, Product-Owner-Spec):
 *   1. Organisation & Struktur — Bereich einleiten
 *   2. Abteilung hinzufügen — Add-Button klicken
 *   3. Abteilungsname — Pflichtfeld, onInput
 *   4. Übergeordnete Abteilung — optionaler Weichensteller, onChange
 *   5. Qualifikationen zuordnen — optional, onChange
 *   6. Speichern — Abschluss mit Brücke zum nächsten Funnel-Schritt
 *
 * Raus: Dialog-Intro-Step, Beschreibungsfeld, Abschluss-Step. Version 2
 * plus allowReplay: bestehende V1-States werden automatisch freigeschaltet.
 */
export const FIRST_DEPARTMENT_ONBOARDING_TOUR: TourConfig = {
  id: TourFeature.FirstDepartment,
  version: 3,
  title: 'CHECKLIST.FIRST_DEPARTMENT.TITLE',
  description: 'CHECKLIST.FIRST_DEPARTMENT.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  // Tour endet im "Abteilung anlegen"-Dialog. Ohne Anlege-Recht hat der User
  // keinen Ziel-Endpunkt — Schritt nicht anbieten.
  requiredPermissions: [['Department.CanCreateDepartments']],
  triggers: [
    TourTrigger.FirstVisit,
    TourTrigger.Resume,
    TourTrigger.Manual,
    TourTrigger.AfterSuccess,
  ],
  // AfterSuccess: wird direkt nach 'company.settings-completed' als Nudge
  // angeboten. Im fachlichen Setup-Funnel der zweite Schritt nach den
  // Unternehmensdaten — Abteilungen sind die strukturelle Voraussetzung
  // fuer Mitarbeiter und Schichtplanung. Skill-Zuordnungen koennen spaeter
  // ueber den Edit-Dialog nachgetragen werden.
  triggerContext: {
    route: '/organization',
    requiresSuccessOf: 'company.settings-completed',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: true,
    cooldownHoursAfterDismiss: 168,
    cooldownHoursAfterSnooze: 24,
    suppressAfterCompletion: true,
  },
  // Priority 20: NACH CompanyInfo (10), VOR AddSkills (30). Reihenfolge
  // synchron zur mainOrder im Checklist-FAB.
  priority: 20,
  estimatedDurationSec: 240,
  category: 'setup',
  chapters: [
    {
      id: 'main',
      title: 'CHECKLIST.FIRST_DEPARTMENT.TITLE',
      steps: [
        {
          id: 'org-overview',
          anchor: AnchorId.OrganizationView,
          title: 'TUTORIAL.DEPARTMENT.V2.OVERVIEW.TITLE',
          content: 'TUTORIAL.DEPARTMENT.V2.OVERVIEW.CONTENT',
          placement: 'top',
          showNextButton: true,
          // Wenn der User die Tour von einem anderen Tab aus startet
          // (typischerweise nach abgeschlossener Skills-Tour ist der Skills-
          // Tab noch aktiv), wechseln wir automatisch auf den Abteilungen-
          // Tab. Sonst sieht der User die Skills-Tabelle umrandet, waehrend
          // der Tooltip "Organisation & Struktur" erklaert — und Step 2
          // findet seinen `add-department-button`-Anchor nicht im DOM,
          // weil der Add-Button-ID je nach activeTab umschaltet.
          onBeforeShow: async () => {
            const tab = document.querySelector<HTMLElement>('[onboardingAnchor="departments-tab"]');
            if (tab && !tab.classList.contains('is-active')) {
              tab.click();
              await new Promise<void>((resolve) =>
                requestAnimationFrame(() => resolve()),
              );
            }
          },
        },
        {
          id: 'add-department',
          // Popover-Anchor = `DepartmentsTable` (Tabellen-Header, immer im
          // DOM und mittig auf der Page, x=124..1466, y=273). Mit
          // `placement: 'bottom'` landet der Popover mittig unterhalb der
          // Tabellen-Spaltenleiste — gleich weit von beiden CTAs entfernt
          // und nicht in den Top-Right- oder Bottom-Mitte-Ecken gefangen.
          //
          // Spotlight bleibt auf beiden Klick-Zielen (Top-Right „+ Neu
          // hinzufügen" + Empty-State „+ Abteilung hinzufügen") — der User
          // sieht klar, dass beide Wege gültig sind.
          anchor: AnchorId.DepartmentsTable,
          spotlightAnchor: AnchorId.AddDepartmentButton,
          additionalSpotlightAnchors: [AnchorId.AddDepartmentEmptyButton],
          // Tour springt zum nächsten Step, sobald der Add-Dialog aufgeht
          // — egal welcher der beiden Buttons geklickt wurde.
          advanceOnDocumentEvent: 'onboarding:department.add-dialog-opened',
          title: 'TUTORIAL.DEPARTMENT.V2.ADD.TITLE',
          content: 'TUTORIAL.DEPARTMENT.V2.ADD.CONTENT',
          placement: 'bottom',
          showNextButton: false,
          // Safety-Net: falls der User in Step 1 den Tab manuell zurueck-
          // gewechselt hat, schalten wir hier nochmal sicher auf Abteilungen.
          // Der Add-Button-Anchor (`add-department-button`) ist nur im
          // Abteilungen-Tab im DOM, sonst findet `waitForAnchor` ihn nicht
          // und die Tour bleibt stehen.
          onBeforeShow: async () => {
            const tab = document.querySelector<HTMLElement>('[onboardingAnchor="departments-tab"]');
            if (tab && !tab.classList.contains('is-active')) {
              tab.click();
              await new Promise<void>((resolve) =>
                requestAnimationFrame(() => resolve()),
              );
            }
          },
        },
        {
          id: 'department-name',
          anchor: AnchorId.DepartmentNameField,
          title: 'TUTORIAL.DEPARTMENT.V2.NAME.TITLE',
          content: 'TUTORIAL.DEPARTMENT.V2.NAME.CONTENT',
          placement: 'bottom',
          nextCondition: 'onInput',
          // Wenn der User von hier aus direkt „Erstellen" klickt (ohne
          // die optionalen Steps 4-5 zu sehen), feuert add-edit-
          // department vor dem Dialog-Close ein Document-Event. Wir
          // advance dann sofort — Steps 4-6 sind via `skipIf` so
          // konfiguriert, dass sie bei geschlossenem Dialog überspringen,
          // bis die Tour beim Outro (Step 7) ankommt.
          advanceOnDocumentEvent: 'onboarding:department.created',
          // Erster Step im offenen Add-Dialog — Vorgaenger zeigt den
          // "+ Hinzufuegen"-Button auf der Page-Ebene, der jetzt vom Dialog
          // verdeckt ist. Zurueck-Sprung waere nicht mehr sichtbar.
          showPrevButton: false,
          // preserveOnClose: Tooltip-X / Esc pausiert NUR die Tour — der
          // Add-Dialog bleibt offen, damit der User seinen bereits getippten
          // Namen nicht verliert (dieser Dialog ist ein aktiv ausgefuelltes
          // Formular, kein blosser Wizard-Schritt). Resume aus der Checkliste
          // findet den noch offenen Dialog vor und setzt exakt hier fort.
          dialogContext: { waitForOpen: true, attachToDialog: true, preserveOnClose: true },
        },
        {
          id: 'parent-department',
          anchor: AnchorId.ParentDepartmentSelect,
          title: 'TUTORIAL.DEPARTMENT.V2.PARENT.TITLE',
          // Empty-State-Awareness: beim Erst-Setup gibt es noch keine
          // übergeordnete Abteilung — die Liste ist leer. Vorher hat der
          // Tooltip stupide "Ordnen Sie die Abteilung ein" gesagt, was
          // fachlich falsch war. Jetzt schaltet der Step zur Render-Zeit
          // zwischen EMPTY (keine Parents) und POPULATED (Parents existieren)
          // um. Bevor die Liste geladen ist, bleiben wir auf der neutralen
          // Default-Variante (CONTENT), um Flacker-Effekte zu vermeiden.
          content: () => {
            const snap = parentDeptSnapshot();
            if (!snap.loaded) return 'TUTORIAL.DEPARTMENT.V2.PARENT.CONTENT';
            return snap.count === 0
              ? 'TUTORIAL.DEPARTMENT.V2.PARENT.CONTENT_EMPTY'
              : 'TUTORIAL.DEPARTMENT.V2.PARENT.CONTENT_POPULATED';
          },
          contentParams: () => ({ count: parentDeptSnapshot().count }),
          // Voice-Over folgt synchron dem Tooltip-Text: spielt die Empty-Spur
          // (noch keine übergeordnete Abteilung vorhanden) bzw. die Populated-
          // Spur, je nach Listenstand. Solange die Liste noch lädt, bleibt es
          // bei der neutralen Basis-Spur `parent-department` — exakt dieselbe
          // Verzweigung wie bei `content`. Die Varianten-Spuren müssen als
          // eigene Einträge im Voice-Over-Manifest existieren
          // (scripts/voiceover-manifest.json → parent-department-empty /
          // parent-department-populated).
          audioStepId: () => {
            const snap = parentDeptSnapshot();
            if (!snap.loaded) return 'parent-department';
            return snap.count === 0
              ? 'parent-department-empty'
              : 'parent-department-populated';
          },
          // Right (statt bottom): mat-select-Optionen klappen nach unten auf;
          // bei placement=bottom wuerde das Popover die Optionen ueberdecken.
          placement: 'right',
          nextCondition: 'onChange',
          // Falls der User aus diesem Step direkt „Erstellen" klickt
          // (Parent-Auswahl ist optional), dispatcht
          // `add-edit-department` synchron `onboarding:department.created`
          // → Tour rueckt sofort vor. Ohne den Listener wuerde der Dialog-
          // Close die Tour ueber `attachToDialog`-Logik als 'close' beenden.
          advanceOnDocumentEvent: 'onboarding:department.created',
          optional: true,
          dialogContext: { attachToDialog: true, preserveOnClose: true },
          // Skip wenn Dialog zu (User hat schon „Erstellen" geklickt).
          skipIf: dialogStepSkipIf,
        },
        {
          id: 'department-skills',
          anchor: AnchorId.DepartmentSkillsSelect,
          title: 'TUTORIAL.DEPARTMENT.V2.SKILLS.TITLE',
          // Analog zu parent-department: Empty-State-Awareness. Wenn noch
          // keine Qualifikationen im System angelegt sind, erklärt der Step
          // den Empty-State + Workaround (Tab "Qualifikationen" → später
          // zuordnen via Bearbeiten-Symbol). Sonst Standard-Erklärung mit
          // Count-Interpolation.
          content: () => {
            const snap = skillsSnapshot();
            if (!snap.loaded) return 'TUTORIAL.DEPARTMENT.V2.SKILLS.CONTENT';
            return snap.count === 0
              ? 'TUTORIAL.DEPARTMENT.V2.SKILLS.CONTENT_EMPTY'
              : 'TUTORIAL.DEPARTMENT.V2.SKILLS.CONTENT_POPULATED';
          },
          contentParams: () => ({ count: skillsSnapshot().count }),
          // Voice-Over analog zu parent-department: Empty-Spur, wenn noch keine
          // Qualifikationen im System angelegt sind, sonst Populated-Spur.
          // Basis-Spur `department-skills` bleibt der neutrale Fallback,
          // solange die Liste lädt. Varianten-Spuren siehe
          // scripts/voiceover-manifest.json (department-skills-empty /
          // department-skills-populated).
          audioStepId: () => {
            const snap = skillsSnapshot();
            if (!snap.loaded) return 'department-skills';
            return snap.count === 0
              ? 'department-skills-empty'
              : 'department-skills-populated';
          },
          // Right (statt bottom): siehe parent-department-Step.
          placement: 'right',
          nextCondition: 'onChange',
          // Backup-Advance: wenn der User aus diesem Step direkt
          // „Erstellen" klickt (Skills-Auswahl ist optional), springt die
          // Tour synchron zum Outro. Sonst wuerde der Dialog-Close die
          // Tour als 'close' beenden.
          advanceOnDocumentEvent: 'onboarding:department.created',
          optional: true,
          dialogContext: { attachToDialog: true, preserveOnClose: true },
          // Skip wenn Dialog zu (User hat schon „Erstellen" geklickt).
          skipIf: dialogStepSkipIf,
        },
        {
          id: 'save-department',
          anchor: AnchorId.SaveDepartmentButton,
          title: 'TUTORIAL.DEPARTMENT.V2.SAVE.TITLE',
          content: 'TUTORIAL.DEPARTMENT.V2.SAVE.CONTENT',
          placement: 'top',
          nextCondition: 'onClick',
          // Backup-Advance via Created-Event: onClick auf den Save-Button
          // sollte normalerweise zuerst feuern, aber als Sicherheitsnetz
          // (z. B. wenn der Klick durch ein Spotlight-Overlay
          // abgefangen wird oder per Keyboard-Submit kommt).
          advanceOnDocumentEvent: 'onboarding:department.created',
          showNextButton: false,
          dialogContext: { attachToDialog: true, preserveOnClose: true },
          // Skip wenn Dialog zu (User hat schon aus früherem Step
          // „Erstellen" geklickt — Step 3's advanceOnDocumentEvent
          // hat Tour hierher gespult, aber Dialog ist bereits weg).
          skipIf: dialogStepSkipIf,
        },
        {
          // Abschluss-Step (analog zu skills-table-overview): erklaert die
          // Departments-Tabelle nach erfolgreichem Save. Wechselt vom Dialog-
          // in den Page-Kontext (kein attachToDialog), 6 Cues highlighten
          // synchron zur Sprache die Spalten Unterabteilungen, Qualifikationen,
          // Manager, Team und Aktionen.
          //
          // Placement: `top` + `align: 'start'` haengt den Popover oben
          // LINKS, sodass die rechten Spalten (Manager, Team, Aktionen),
          // die der Voice-Over zelebriert, frei sichtbar bleiben inkl.
          // Pulse-Ring. Ein 'top center'-Default wuerde die Mitte verdecken.
          //
          // `onBeforeShow` wartet 250 ms, damit die Material-Dialog-Close-
          // Animation durch ist, bevor das Spotlight auf der Tabelle landet.
          id: 'departments-table-overview',
          anchor: AnchorId.DepartmentsTable,
          title: 'TUTORIAL.DEPARTMENT.V2.TABLE_OVERVIEW.TITLE',
          content: 'TUTORIAL.DEPARTMENT.V2.TABLE_OVERVIEW.CONTENT',
          placement: 'top',
          align: 'start',
          nextCondition: 'manual',
          showNextButton: true,
          // Page-Step nach Dialog-Close — Vorgaenger (Speichern-Button) lebte
          // im inzwischen geschlossenen Add-Dialog, Zurueck-Sprung waere ohne
          // Anchor.
          showPrevButton: false,
          onBeforeShow: waitForDeptDialogGone,
        },
      ],
    },
  ],
};

export const DEPARTMENT_ADD_CONTEXTUAL_TOUR: TourConfig = {
  id: TourFeature.DepartmentAddContextual,
  version: 2,
  title: 'ADD_DEPARTMENT',
  description: 'DEPARTMENT_CONTEXTUAL_TOUR_DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  triggers: [TourTrigger.Manual],
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: false,
    cooldownHoursAfterDismiss: 0,
    cooldownHoursAfterSnooze: 0,
    suppressAfterCompletion: false,
  },
  priority: 10,
  estimatedDurationSec: 90,
  category: 'contextual-help',
  chapters: [
    {
      id: 'basics',
      title: 'BASIC',
      steps: [
        {
          id: 'parent',
          anchor: AnchorId.ParentDepartmentAddDepartment,
          title: 'ADD_DEPARTMENT_PARENT_DEPARTMENT_LABEL',
          content: 'ADD_DEPARTMENT_PARENT_DEPARTMENT_DESCRIPTION',
          placement: 'left',
          dialogContext: { attachToDialog: true },
        },
        {
          id: 'skills',
          anchor: AnchorId.SkillIdsAddDepartment,
          title: 'ADD_DEPARTMENT_ABILITY_COMPETENCE_LABEL',
          content: 'ADD_DEPARTMENT_ABILITY_COMPETENCE_DESCRIPTION',
          placement: 'right',
          dialogContext: { attachToDialog: true },
        },
      ],
    },
    {
      id: 'project',
      title: 'PROJECT',
      steps: [
        {
          id: 'project-manager',
          anchor: AnchorId.ProjectManagerAddDepartment,
          title: 'ADD_DEPARTMENT_PROJECT_MANAGER_LABEL',
          content: 'ADD_DEPARTMENT_PROJECT_MANAGER_DESCRIPTION',
          placement: 'left',
          dialogContext: { attachToDialog: true },
        },
        {
          id: 'project-end-date',
          anchor: AnchorId.ProjectEndDateAddDepartment,
          title: 'ADD_DEPARTMENT_PROJECT_END_DATE_LABEL',
          content: 'ADD_DEPARTMENT_PROJECT_END_DATE_DESCRIPTION',
          placement: 'right',
          dialogContext: { attachToDialog: true },
          nextCondition: 'onInput',
          autoAdvance: AUTO_ADVANCE_ON_BLUR,
        },
        {
          id: 'project-status',
          anchor: AnchorId.ProjectStatusAddDepartment,
          title: 'ADD_DEPARTMENT_PROJECT_STATUS_LABEL',
          content: 'ADD_DEPARTMENT_PROJECT_STATUS_DESCRIPTION',
          placement: 'left',
          dialogContext: { attachToDialog: true },
        },
      ],
    },
    {
      id: 'admin',
      title: 'ADD_DEPARTMENT_MANAGE_QUALIFICATIONS_TITLE',
      steps: [
        {
          id: 'manage-qualifications',
          anchor: AnchorId.ManageQualificationsAddDepartment,
          title: 'ADD_DEPARTMENT_MANAGE_QUALIFICATIONS_TITLE',
          content: 'ADD_DEPARTMENT_EXPLANATION_CATEGORY_DEPARTMENT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true },
        },
        {
          id: 'manage-projects',
          anchor: AnchorId.ManageProjectsAddDepartment,
          title: 'ADD_DEPARTMENT_MANAGE_PROJECTS_TITLE',
          content: 'ADD_DEPARTMENT_EXPLANATION_CATEGORY_PROJECT',
          placement: 'bottom',
          dialogContext: { attachToDialog: true },
        },
      ],
    },
  ],
};
